import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatPrice } from "../lib/api";
import HolographicPlaceholder from "../components/HolographicPlaceholder";
import { CheckCircle, Sparkles } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let email = "";
    try { email = sessionStorage.getItem(`kavo_order_${id}`) || ""; } catch {}
    const url = email ? `/orders/${id}?email=${encodeURIComponent(email)}` : `/orders/${id}`;
    api.get(url).then(({ data }) => setOrder(data)).catch(() => setOrder(null));
  }, [id]);

  if (!order) return <div className="max-w-[800px] mx-auto px-6 py-20 text-white/60">Loading order…</div>;

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-16">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#B026FF]/40 bg-[#B026FF]/10 text-[#E1B7FF] text-sm">
          <CheckCircle size={16} className="text-[#B026FF]" /> Order Confirmed
        </div>
        <h1 className="mt-6 font-display font-extrabold text-4xl lg:text-6xl tracking-tighter text-white">
          Thank you, <span className="text-gradient-violet">{order.name?.split(" ")[0]}</span>
        </h1>
        <p className="mt-4 text-white/60 max-w-md mx-auto">
          Your order <span className="font-mono text-white">{order.order_number}</span> is being prepared.
          A confirmation has been sent to {order.email}.
        </p>
        {order.points_earned > 0 && (
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#B026FF]">
            <Sparkles size={14} /> You earned <span className="font-mono">{order.points_earned}</span> Kavo Points
          </div>
        )}
      </div>

      <div className="mt-14 card-elev rounded-xl p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pb-6 border-b border-white/5">
          <SummaryItem label="Order #" value={order.order_number} />
          <SummaryItem label="Status" value={order.status?.toUpperCase()} accent />
          <SummaryItem label="Total" value={formatPrice(order.total_cents)} />
        </div>

        <ul className="divide-y divide-white/5 mt-2">
          {order.items.map((it) => (
            <li key={it.product_id} className="py-4 flex gap-4 items-center">
              <div className="w-16 shrink-0">
                <HolographicPlaceholder label={it.name} ratio="aspect-[3/4]" imageUrl={it.image_url} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#B026FF]/80 font-bold">{it.category?.replace(/-/g, " ")}</div>
                <div className="text-white truncate">{it.name}</div>
                <div className="text-xs text-white/50 font-mono">× {it.quantity}</div>
              </div>
              <div className="font-mono text-white">{formatPrice(it.line_total_cents)}</div>
            </li>
          ))}
        </ul>

        <div className="mt-6 pt-5 border-t border-white/5 space-y-2 max-w-sm ml-auto text-sm">
          <Row label="Subtotal" value={formatPrice(order.subtotal_cents)} />
          {order.discount_cents > 0 && <Row label="Kavo Points" value={`- ${formatPrice(order.discount_cents)}`} accent />}
          <Row label="Shipping" value={order.shipping_cents === 0 ? "Free" : formatPrice(order.shipping_cents)} />
          <Row label="Tax" value={formatPrice(order.tax_cents)} />
          <div className="flex items-center justify-between text-base pt-3 border-t border-white/5 mt-2">
            <span className="text-white">Total</span>
            <span className="font-mono text-white">{formatPrice(order.total_cents)}</span>
          </div>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link to="/shop" data-testid="order-continue-shop" className="btn-ghost-violet inline-flex h-12 px-7 rounded-md items-center gap-2 font-medium">
          Continue shopping
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-white/60">{label}</span>
      <span className={`font-mono ${accent ? "text-[#B026FF]" : "text-white"}`}>{value}</span>
    </div>
  );
}
function SummaryItem({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 font-bold">{label}</div>
      <div className={`mt-1 font-mono ${accent ? "text-[#B026FF]" : "text-white"}`}>{value}</div>
    </div>
  );
}
